import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Flex, UnstyledButton } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import type { InfiniteData, QueryKey } from '@tanstack/react-query'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { useUserStore } from '@/store/user.store'

import {
  useChatSocket,
  useUpdateMessages,
} from '@/components/@Shared/Chat/Chat.config'
import {
  type ChatProps,
  ConnectionState,
  type IncomingMessage,
} from '@/components/@Shared/Chat/Chat.types'
import { ChatMessageInput } from '@/components/@Shared/Chat/partials/ChatMessageInput/ChatMessageInput'
import { ChatMessages } from '@/components/@Shared/Chat/partials/ChatMessages/ChatMessages'
import { Loader } from '@/components/Loader/Loader'
import { Text } from '@/components/Text/Text'

import { useMessageServiceMessageControllerGetAllMessagesKey } from '@/__generated__/queries'
import { type MessageData, MessageService } from '@/__generated__/requests'

import CloseIcon from '@/assets/icons/close.svg?react'

import classes from './Chat.module.css'

const INPUT_PLACEHOLDER_BY_STATE: Record<ConnectionState, string> = {
  [ConnectionState.CLOSED]: 'Disconnected',
  [ConnectionState.CONNECTING]: 'Connecting…',
  [ConnectionState.OPEN]: 'Write a message...',
  [ConnectionState.RECONNECTING]: 'Reconnecting…',
}

export function Chat({
  onChatClose,
  recipientId,
  recipientImage,
  recipientName,
}: ChatProps) {
  const updateMessages = useUpdateMessages()
  const queryClient = useQueryClient()

  const user = useUserStore.use.user()

  const userRef = useRef(user)

  const parseMessages = useCallback((data: MessageData[][]) => {
    const uniqueMessagesMap = new Map<string | number, MessageData>()

    data.forEach(page => {
      page.forEach(message => {
        if (!uniqueMessagesMap.has(message.id)) {
          uniqueMessagesMap.set(message.id, message)
        } else {
          uniqueMessagesMap.set(message.id, {
            ...uniqueMessagesMap.get(message.id),
            ...message,
          })
        }
      })
    })

    const uniqueMessages = Array.from(uniqueMessagesMap.values())

    return uniqueMessages.reverse()
  }, [])

  const { data, fetchNextPage, fetchStatus, isFetchingNextPage, isLoading } =
    useInfiniteQuery<
      MessageData[],
      unknown,
      InfiniteData<MessageData[], number>,
      QueryKey,
      number
    >({
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        if (lastPage.length < 50) return undefined

        return lastPageParam + 1
      },
      initialPageParam: 1,
      queryFn: ({ pageParam = 1 }) =>
        MessageService.messageControllerGetAllMessages({
          recipientId,
          skip: (pageParam - 1) * 50,
          take: 50,
        }),
      queryKey: [useMessageServiceMessageControllerGetAllMessagesKey],
    })

  const messages = useMemo(
    () => parseMessages(data?.pages.reverse() ?? []),
    [data?.pages, parseMessages],
  )

  const isFetching =
    isLoading || isFetchingNextPage || fetchStatus === 'fetching'

  const markThreadRead = useCallback(() => {
    // Best-effort: tells the backend the participant has seen the advisor's
    // messages so the #14 advisor-message email is suppressed. Must never
    // disrupt the chat, so failures are swallowed.
    MessageService.messageControllerMarkThreadRead({ recipientId }).catch(
      () => undefined,
    )
  }, [recipientId])

  const handleIncomingMessage = useCallback(
    (message: IncomingMessage) => {
      if (!userRef.current) return

      updateMessages({
        content: message.content,
        id: message.id,
        recipientId: userRef.current.id,
        time: dayjs().toISOString(),
        userId: recipientId,
      })

      // The message arrived while the chat is open, so it's seen immediately —
      // mark it read to suppress the email notification.
      markThreadRead()
    },
    [markThreadRead, recipientId, updateMessages],
  )

  const { connectionState, sendMessage } = useChatSocket({
    onMessage: handleIncomingMessage,
    recipientId,
  })

  const handleSendMessage = useCallback(
    (content: string) => {
      if (!userRef.current) return

      if (!sendMessage(content)) {
        notifications.show({
          color: 'red',
          message: 'Connection is not established',
          title: 'Error',
        })

        return
      }

      updateMessages({
        content,
        id: dayjs().valueOf(),
        recipientId,
        time: dayjs().toISOString(),
        userId: userRef.current.id,
      })
    },
    [recipientId, sendMessage, updateMessages],
  )

  useEffect(() => {
    userRef.current = user
  }, [user])

  useEffect(() => {
    queryClient.resetQueries({
      queryKey: [useMessageServiceMessageControllerGetAllMessagesKey],
    })
  }, [queryClient, recipientId])

  // Opening (or switching to) a conversation means the participant is viewing
  // it, so mark its messages read to suppress the advisor-message email.
  useEffect(() => {
    markThreadRead()
  }, [markThreadRead])

  const isInputDisabled = connectionState !== ConnectionState.OPEN

  return (
    <Flex className={classes.container}>
      <Flex className={classes.header}>
        <Text variant="body_1_semibold">{recipientName}</Text>

        <UnstyledButton className={classes.closeButton} onClick={onChatClose}>
          <CloseIcon />
        </UnstyledButton>
      </Flex>

      <Flex className={classes.disclaimer}>
        <Text variant="body_2_medium_green">
          The recipient may not be online at the moment. Your message will be
          delivered, and they will respond as soon as they are available.
        </Text>
      </Flex>

      {!isFetchingNextPage && (isLoading || isFetching) ? (
        <Loader />
      ) : (
        <ChatMessages
          fetchNextPage={fetchNextPage}
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading || isFetching}
          messages={messages}
          recipientImage={recipientImage}
          senderId={user?.id}
        />
      )}

      <ChatMessageInput
        disabled={isInputDisabled}
        onMessageSend={handleSendMessage}
        placeholder={INPUT_PLACEHOLDER_BY_STATE[connectionState]}
      />
    </Flex>
  )
}
